import { useUserMe } from "~/components/user/composables/useUser";
import { useSupabase } from "~/composables/useSupabase";
import { useToast } from "~/composables/useToast";
import { type NotificationData, generateNotificationText } from "@asap/shared/utils/notification";
import { notificationColumns } from "@asap/shared/utils/supabase.columns";
import type { Notification } from "@asap/shared/utils/supabase.types";
import type { DeepPartial } from "~/utils/deepPartial";

export type NotificationFilterType = "business" | "talent" | "marketing" | "internal" | "all";

/**
 * The feed shows what was delivered to the back office. Asking the delivery rows rather than
 * listing types keeps one source of truth: a notification sent on `email` only — the saved-search
 * récap — never surfaces here, and no list has to be maintained alongside the channels.
 *
 * Rows written by the legacy notification path carry no delivery at all, so "no delivery" stays
 * visible for as long as both paths coexist.
 */
const deliveredToBackOffice = (deliveries: { channel: string }[]): boolean =>
  deliveries.length === 0 || deliveries.some((delivery) => delivery.channel === "back_office");

// Don't forget to update the notificationFilterMap when you add a new notification type
const notificationFilterMap: Record<NotificationData["type"], NotificationFilterType[]> = {
  mobile_app__talent_new_final_pay_slip: ["talent"],
  mobile_app__talent_contract_signed: ["business"],
  mobile_app__timesheet_fully_approved: ["business"],
  mobile_app__talent_timesheet_reminder: ["business"],
  mobile_app__timesheet_rejected_by_client: ["business"],
  mobile_app__timesheet_reset_signatures: ["talent"],
  timesheet_reset_signatures: ["talent"],
  mobile_app__expense_claim_refused: ["internal"],
  mobile_app__expense_claim_approved: ["internal"],
  mobile_app__expense_claim_canceled: ["internal"],
  mobile_app__talent_contract_unsigned_reminder: ["talent"],
  new_contract_to_process: ["business"],
  new_definitive_end_on_contract_to_process: ["business"],
  new_medical_checkup_request: ["talent"],
  cancel_medical_checkup_request_on_definitive_contract_end: ["talent"],
  medical_checkup_request_done: ["talent"],
  medical_checkup_request_approved: ["talent"],
  medical_checkup_request_pending: ["talent"],
  medical_checkup_request_needed: ["talent"],
  failed_advance_payment_request: ["talent"],
  new_qualification_training_request: ["talent"],
  new_expense_claim_request_to_validate: ["internal"],
  new_expense_claim_request_to_process: ["internal"],
  new_professional_card_request: ["business"],
  talent_offboarding_needed: ["talent"],
  approved_recruitment_office_invoice_request: ["business"],
  refused_recruitment_office_invoice_request: ["business"],
  new_recruitment_office_invoice_request: ["business"],
  pending_recruitment_office_invoice_request: ["business"],
  done_recruitment_office_invoice_request: ["business"],
  recruitment_office_payment_paid: ["business"],
  new_contract_to_send: ["business"],
  new_onboarding_app_needed: ["talent"],
  new_advance_payment_request: ["talent"],
  new_company_insurance_request: ["internal"],
  end_of_company_insurance_request: ["internal"],
  first_contract_signed_by_talent: ["business"],
  new_qualification_training_request_invoicing_document: ["talent"],
  new_qualification_training_request_certificate_document: ["talent"],
  cancel_ppe_request_on_definitive_contract_end: ["talent"],
  cancel_qualif_train_req_on_definitive_contract_end: ["talent"],
  failed_website_job_posting: ["marketing"],
  first_contract_signed_by_independent: ["business"],
  new_qualification_training_commitment: ["talent"],
  new_contract_document_pre_employment_declaration: ["business"],
  new_professional_card_request_document_uploaded: ["talent"],
  professional_card_request_completed: ["talent"],
  professional_card_request_refused: ["talent"],
  professional_card_photo_rejected: ["talent"],
  professional_card_request_resubmitted: ["talent"],
  mobile_app__professional_card_photo_rejected: ["talent"],
  professional_card_photo_missing: ["talent"],
  mobile_app__professional_card_photo_reminder: ["talent"],
  new_mission_to_post_on_job_boards: ["marketing"],
  archived_job_offer: ["marketing"],
  new_contract_signed: ["business"],
  new_contract_declined: ["business"],
  credit_insurer_cover_refused: ["business"],
  credit_insurer_cover_approved: ["business"],
  credit_insurer_cover_amount_changed: ["business"],
  expiring_document: ["talent"],
  new_commercial_condition_created: ["business"],
  new_contract_to_validate: ["business"],
  new_contract_to_validate_information: ["business"],
  new_ppe_request: ["talent"],
  new_ppe_request_from_app: ["talent"],
  new_ppe_request_validated: ["talent"],
  each_commercial_condition_validated: ["business"],
  commercial_conditions_is_signed: ["business"],
  commercial_conditions_is_declined: ["business"],
  job_offer_republish: ["marketing"],
  new_request_push_marketing: ["marketing"],
  turned_off_push_marketing: ["marketing"],
  new_work_accident_request: ["talent"],
  work_accident_request_done: ["talent"],
  qualification_training_request_approved: ["talent"],
  new_rib_uploaded: ["talent"],
  // Slack-only: never delivered to the back-office feed, but the map is exhaustive.
  team_member_offboarding_progressed: ["internal"],
  talent_plug_unknown_source_encountered: ["internal"],
  contract_canceled_by_ops: ["internal"],
  talents_merged_with_contract: ["business"],
  talent_documents_uploaded: ["talent"],
  expense_claim_to_review: ["talent"],
  pay_tracking_completed: ["business"],
  pay_tracking_processed: ["business"],
  pay_tracking_checked: ["business"],
  timesheet_contact_signer_error: ["talent"],
  expense_claim_request_to_confirm_reminder: ["talent"],
  to_modify_by_owner: ["business"],
  contract_validated: ["business"],
  modified_contract_to_validate: ["business"],
  modified_contract_to_send: ["business"],
  new_contract_to_modify_by_admin: ["business"],
  new_amendment_to_validate: ["business"],
  inform_manager_on_new_contract_created_without_validation: ["business"],
  advance_payment_request_payment_failed: ["business"],
  advance_payment_request_payment_refused: ["business"],
  advance_payment_pld_batch_send_completed: ["business"],
  third_party_notice_bulk_import_completed: ["business"],
  // Sourcing is the consultant's own business activity, not a talent-lifecycle event.
  new_talents_matching_saved_talent_search: ["business"],
  timesheet_pld_batch_send_completed: ["business"],
  extension_amendment_pld_batch_send_completed: ["business"],
  // E-mail-only: it never lands in the back-office feed, but the map must stay exhaustive.
  saved_talent_search_daily_summary: ["business"],
  timesheet_proof_uploaded_by_talent: ["business"],
  timesheet_reset_for_admin: ["business"],
  document_external_review_rejected: ["talent"],
  talent_identity_document_uploaded: ["talent"],
  mobile_app__talent_document_rejected: ["talent"],
};

// We omit the json data field cause it will be pick from the NotificationData type (and Json type is too complex for vue in some components)
export type NotificationCasted = Omit<Notification, "data"> &
  DeepPartial<NotificationData> & {
    filterType: NotificationFilterType[];
  };

const castNotification = (notification: Notification): NotificationCasted => {
  const filterType: NotificationFilterType[] = notificationFilterMap[notification.type] ?? [];
  return { ...notification, filterType: [...filterType, "all"] } as NotificationCasted;
};

const notifications = ref<NotificationCasted[]>([]);
const countUnread = ref(0);
const selectedFilter = ref<NotificationFilterType>("all");
const filterOptions = ref({ isRead: false });

export const useNotification = function () {
  const { authUser, userRoles } = useUserMe();
  const { addToastError } = useToast();
  const { t } = useI18n();
  const supabase = useSupabase();

  const loading = ref(true);

  const queryUserNotifications = async (): Promise<NotificationCasted[]> => {
    if (!authUser.value) {
      addToastError({
        description: t("notifications.errors.accountUnavailable"),
        title: t("notifications.errors.slackConnection"),
      });
      throw new Error(t("notifications.errors.accountUnavailable"));
    }

    loading.value = true;

    const { data, error } = await supabase
      .from("notification")
      .select(`${notificationColumns}, notification_delivery(channel)`)
      .or(`recipient_user.eq.${authUser.value.id},recipient_roles.ov.{${userRoles.value}}`)
      .order("created_at", { ascending: false });

    loading.value = false;

    if (error) {
      addToastError({ description: t("notifications.errors.fetchFailed") }, error);
      throw error;
    }

    return data.filter((row) => deliveredToBackOffice(row.notification_delivery)).map(castNotification);
  };

  const loadNotifications = async () => {
    const loaded = await queryUserNotifications();
    notifications.value = loaded;
    // The badge counts the feed, which depends on each notification's deliveries.
    // The list query already applied that filter, so a second unread query is redundant.
    countUnread.value = loaded.filter((notification) => !notification.is_read).length;
  };

  onMounted(() => {
    void loadNotifications();
  });

  const markAsRead = async (notificationToUpdate: NotificationCasted) => {
    notifications.value = updateCollection(notifications.value, { ...notificationToUpdate, is_read: true });

    const { error } = await supabase
      .from("notification")
      .update({ is_read: true })
      .eq("id", notificationToUpdate.id)
      .select()
      .single();

    if (error) {
      addToastError({ description: t("notifications.errors.pageNotFound") });
      throw error;
    }

    countUnread.value = Math.max(0, countUnread.value - 1);
  };

  const navigateToLink = (notification: NotificationCasted) => {
    const url = notification.data?.navigateTo;

    if (!url) {
      addToastError({ description: t("notifications.errors.pageNotFound") });
      throw new Error(t("notifications.errors.pageNotFound"));
    }

    const isExternal = url.startsWith("http");
    if (isExternal) {
      navigateTo(url, { external: true });
      return;
    }

    // Nuxt needs `{ path, query }` so pages can read route.query.
    // A bare string URL with `?…` does not reliably populate route.query.
    const [path, searchParams = ""] = url.split("?");
    if (searchParams.length === 0) {
      navigateTo(path);
      return;
    }

    const query = Object.fromEntries(new URLSearchParams(searchParams));
    navigateTo({ path, query });
  };

  const markAllAsRead = async () => {
    if (!authUser.value) {
      addToastError({
        description: t("notifications.errors.accountUnavailable"),
        title: t("notifications.errors.slackConnection"),
      });
      throw new Error(t("notifications.errors.accountUnavailable"));
    }

    notifications.value = notifications.value.map((notification: any) => ({ ...notification, is_read: true }));

    const { error } = await supabase
      .from("notification")
      .update({ is_read: true })
      .or(`recipient_user.eq.${authUser.value.id},recipient_roles.ov.{${userRoles.value}}`);
    if (error) {
      addToastError({ description: t("notifications.errors.markAsReadFailed") });
      throw error;
    }

    countUnread.value = 0;
  };

  const unreadNotifications = computed(() => notifications.value.filter((notification: any) => !notification.is_read));

  const isNotificationBadged = (notification: NotificationCasted) => {
    return notification.data?.workspace === "external";
  };

  const filterTabs: { filterType: NotificationFilterType; label: string; count: number }[] = [
    { filterType: "all", label: t("notifications.filters.all"), count: 0 },
    { filterType: "business", label: t("notifications.filters.business"), count: 0 },
    { filterType: "talent", label: t("notifications.filters.talent"), count: 0 },
    { filterType: "marketing", label: t("notifications.filters.marketing"), count: 0 },
    { filterType: "internal", label: t("notifications.filters.internal"), count: 0 },
  ];

  const computedFilterTabs = computed(() => {
    return filterTabs.map((tab) => {
      return {
        ...tab,
        // @ts-expect-error Type instantiation is excessively deep and possibly infinite
        count: notifications.value.filter(
          (notification) =>
            notification.filterType.includes(tab.filterType) && notification.is_read === filterOptions.value.isRead
        ).length,
      };
    });
  });

  const computedFilterTabsUnread = computed(() => {
    return filterTabs.map((tab) => {
      return {
        ...tab,
        count: unreadNotifications.value.filter(
          (notification) =>
            notification.filterType.includes(tab.filterType) && notification.is_read === filterOptions.value.isRead
        ).length,
      };
    });
  });

  const filteredNotifications = computed(() => {
    return notifications.value.filter(
      (notification) =>
        notification.filterType.includes(selectedFilter.value) && notification.is_read === filterOptions.value.isRead
    );
  });

  const filteredUnreadNotifications = computed(() => {
    return unreadNotifications.value.filter((notification) => notification.filterType.includes(selectedFilter.value));
  });

  return {
    notifications,
    unreadNotifications,
    countUnread,
    loading,
    markAsRead,
    markAllAsRead,
    navigateToLink,
    refresh: loadNotifications,
    generateNotificationText,
    isNotificationBadged,
    computedFilterTabs,
    computedFilterTabsUnread,
    filteredNotifications,
    filteredUnreadNotifications,
    selectedFilter,
    filterOptions,
  };
};
